/* global debounce */

if (!customElements.get('search-form')) {
  class SearchForm extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = document.getElementById('product_type_input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.addListeners();
      this.init();
    }

    connectedCallback() {
      this.form = this.closest('form');
      this.restoreSearchTerm();
      this.submitHandler = this.submitHandler || this.handleSubmit.bind(this);
      this.form?.addEventListener('submit', this.submitHandler);
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.submitHandler);
      this.cancelSubmission();
    }

    restoreSearchTerm() {
      if (this.dataset.persistValue !== 'true') return;
      const query = new URLSearchParams(window.location.search).get('q');
      if (!query) return;
      try {
        const enteredTerm = sessionStorage.getItem(`search-term:${window.location.pathname}:${query}`);
        if (enteredTerm !== null) this.input.value = enteredTerm;
      } catch (error) {
        // The server-rendered code remains readable when browser storage is unavailable.
      }
    }

    addListeners() {
      this.input.addEventListener('input', debounce(this.handleInput.bind(this)));
      this.input.addEventListener('input', this.cancelSubmission.bind(this));
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.resetBtn.addEventListener('click', this.handleReset.bind(this));
    }

    /**
     * Confirm a code against full search before its one normal page navigation.
     * Autocomplete requests and clicks are handled separately by predictive-search.
     */
    async handleSubmit(evt) {
      if (this.submittingResolvedQuery || evt.defaultPrevented) return;
      const query = this.input.value.trim();
      if (!/^(?=.*\d)[a-z\d._/-]+$/i.test(query)) return;
      const predictiveSearch = this.closest('predictive-search');
      if (predictiveSearch?.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')) return;

      const action = new URL(this.form.action, window.location.href);
      if (action.origin !== window.location.origin) return;
      evt.preventDefault();
      if (this.pendingSubmission) return;

      const params = new URLSearchParams(new FormData(this.form));
      const snapshot = params.toString();
      const pending = { controller: new AbortController() };
      this.pendingSubmission = pending;
      this.setSubmitting(true);
      // A slow or unavailable search service must not prevent a normal search.
      const timeout = setTimeout(() => pending.controller.abort(), 3000);

      try {
        const candidates = [`variants.sku:"${query}"`];
        if (/^\d+$/.test(query)) candidates.push(`variants.sku:"LR-${query}"`);
        candidates.push(`tag:"mfg_code:${query}"`, `tag:"mfg_code: ${query}"`);
        const matches = await Promise.all(candidates.map(async (candidate) => {
          const url = new URL(action);
          url.search = params.toString();
          url.searchParams.set('q', candidate);
          url.searchParams.delete('page');
          url.searchParams.delete('sections');
          url.searchParams.set('section_id', 'search-code-check');
          try {
            const response = await fetch(url, { signal: pending.controller.signal });
            if (!response.ok) return false;
            const template = document.createElement('template');
            template.innerHTML = await response.text();
            const count = template.content.querySelector('[data-search-code-count]');
            return count !== null && Number(count.textContent) > 0;
          } catch (error) {
            return false;
          }
        }));

        if (this.pendingSubmission !== pending || !this.isConnected
          || new URLSearchParams(new FormData(this.form)).toString() !== snapshot) return;
        const matchIndex = matches.findIndex(Boolean);
        const resolvedQuery = matchIndex < 0 ? query : candidates[matchIndex];
        // Override only the submitted value; do not change the visible input or
        // dispatch input events that would alter the autocomplete dropdown.
        const setQuery = (event) => {
          event.formData.set('q', resolvedQuery);
          try {
            // Keep the customer's entry across navigation, filtering and reloads.
            // The resolved query is still submitted to Shopify for the results.
            sessionStorage.setItem(`search-term:${action.pathname}:${resolvedQuery}`, this.input.value);
          } catch (error) {
            // Storage restrictions must not prevent the search from submitting.
          }
        };
        this.form.addEventListener('formdata', setQuery);
        this.submittingResolvedQuery = true;
        try {
          const submitter = evt.submitter?.form === this.form ? evt.submitter : undefined;
          this.form.requestSubmit(submitter);
        } finally {
          this.form.removeEventListener('formdata', setQuery);
          this.submittingResolvedQuery = false;
        }
      } finally {
        clearTimeout(timeout);
        if (this.pendingSubmission === pending) {
          this.pendingSubmission = null;
          this.setSubmitting(false);
        }
      }
    }

    setSubmitting(pending) {
      if (pending) this.form.setAttribute('aria-busy', 'true');
      else this.form.removeAttribute('aria-busy');
      this.querySelector('.search__submit')?.classList.toggle('is-loading', pending);
    }

    cancelSubmission() {
      if (!this.pendingSubmission) return;
      this.pendingSubmission.controller.abort();
      this.pendingSubmission = null;
      this.setSubmitting(false);
    }

    init() {
      if ((this.input.dataset.placeholderTwo || this.input.dataset.placeholderThree)
        && (this.input.dataset.placeholderPromptsMob === 'true' || theme.mediaMatches.md)) {
        this.typeInPlaceholders();
      }
    }

    /**
     * Simulates the placeholder titles being typed in and the previous one deleted, on repeat
     */
    typeInPlaceholders() {
      const typingSpeed = this.input.dataset.typingSpeed || 100;
      const deletingSpeed = this.input.dataset.deletingSpeed || 60;
      const delayAfterDeleting = this.input.dataset.delayAfterDeleting || 500;
      const delayBeforeFirstDelete = this.input.dataset.delayBeforeFirstDelete || 2000;
      const delayAfterWordTyped = this.input.dataset.delayAfterWordTyped || 2400;
      const placeholders = [];

      if (this.input.placeholder) {
        placeholders.push(this.input.placeholder);
      }

      if (this.input.dataset.placeholderTwo) {
        placeholders.push(this.input.dataset.placeholderTwo);
      }

      if (this.input.dataset.placeholderThree) {
        placeholders.push(this.input.dataset.placeholderThree);
      }

      const typeInNextPlaceholder = async (placeholder) => {
        await new Promise((resolve) => {
          let currentPlaceholder = this.input.getAttribute('placeholder');

          // If necessary remove the start of the word to type in if it already exists in the
          // placeholder
          let nextPlaceholder = (currentPlaceholder.length >= 3
              && placeholder.startsWith(currentPlaceholder))
            ? placeholder.replace(currentPlaceholder, '')
            : placeholder;

          const typingIntervalId = setInterval(() => {
            currentPlaceholder = this.input.getAttribute('placeholder');
            this.input.setAttribute('placeholder', currentPlaceholder + nextPlaceholder.charAt(0));
            if (nextPlaceholder.length === 1) {
              resolve();
              clearInterval(typingIntervalId); // Stop typing
            } else {
              nextPlaceholder = nextPlaceholder.substring(1);
            }
          }, typingSpeed);
        });
      };

      const deleteCurrentPlaceholder = async (nextPlaceholder) => {
        await new Promise((resolve) => {
          let prevPlaceholder = this.input.getAttribute('placeholder');
          const deletionIntervalId = setInterval(() => {
            const newPlaceholder = prevPlaceholder.substring(0, prevPlaceholder.length - 1);
            this.input.setAttribute('placeholder', newPlaceholder);
            prevPlaceholder = newPlaceholder;
            if (prevPlaceholder.length === 0
              || (prevPlaceholder.length >= 3 && nextPlaceholder.startsWith(prevPlaceholder))) {
              resolve();
              clearInterval(deletionIntervalId);
            }
          }, deletingSpeed);
        });
      };

      let startIndex = 0;
      const showNextPlaceholder = () => {
        startIndex = (startIndex + 1) % placeholders.length;
        const nextPlaceholder = placeholders[startIndex];
        deleteCurrentPlaceholder.call(this, nextPlaceholder).then(() => {
          setTimeout(() => {
            typeInNextPlaceholder.call(this, nextPlaceholder).then(() => {
              setTimeout(showNextPlaceholder.bind(this), delayAfterWordTyped);
            });
          }, delayAfterDeleting);
        });
      };

      setTimeout(showNextPlaceholder.bind(this), delayBeforeFirstDelete);
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      if (this.input.value.length > 0) {
        this.input.classList.add('search__input--dirty');
      } else {
        this.input.classList.remove('search__input--dirty');
      }
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.cancelSubmission();
      this.productTypeInput.value = evt.detail.selectedValue;
    }

    /**
     * Handles the reset button being clicked
     */
    handleReset() {
      this.cancelSubmission();
      this.input.classList.remove('search__input--dirty');
    }
  }

  customElements.define('search-form', SearchForm);
}
